/**
 * WeatherService — Open-Meteo client with stale-while-revalidate caching.
 *
 * Single network call returns: current + hourly (24h) + daily (10d) + AQI.
 * The full payload is cached under one key per location; getData() returns
 * a slim WeatherData shape for the home widget, getDetails() returns the
 * full WeatherDetails for the dedicated Weather page. No double-fetch.
 *
 * APIs used (free, no key, CORS-clean):
 *   - api.open-meteo.com/v1/forecast      — weather
 *   - air-quality-api.open-meteo.com/v1/  — AQI + particulates
 *   - geocoding-api.open-meteo.com/v1/    — city search (geocodeCity)
 */

import type { WeatherData, WeatherDetails } from '../modules/home/types';
import type { LocationService } from './LocationService';
import { swr, swrPeek } from './swr';
import { getLunarState, lunarLabelKey } from '../modules/weather/astronomy';

const FRESH_TTL_MS = 30 * 60 * 1000;
const STALE_TTL_MS = 24 * 60 * 60 * 1000;

function cacheKey(lat: number, lng: number): string {
  // Round to ~1 km. Privacy + cache hits across nearby coords.
  return `weather-v2:${lat.toFixed(2)}:${lng.toFixed(2)}`;
}

interface GeocodeResult {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
  country?: string;
  country_code?: string;
  admin1?: string;
  admin2?: string;
  population?: number;
  timezone?: string;
}

export class WeatherService {
  private data: WeatherData | null = null;
  private detailsCache: WeatherDetails | null = null;
  private locationService: LocationService;
  private listeners: Array<() => void> = [];

  constructor(locationService: LocationService) {
    this.locationService = locationService;
    this.loadFromCache();
  }

  getData(): WeatherData | null {
    return this.data;
  }

  /** Subscribe to weather data updates. Fires when fresh data lands from
   *  the network (after a stale-while-revalidate fetch resolves) so UI
   *  surfaces showing the weather chip can re-render the moment cached
   *  "—" turns into a real temperature. */
  onChange(fn: () => void): () => void {
    this.listeners.push(fn);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== fn);
    };
  }

  private notify(): void {
    for (const fn of this.listeners) {
      try { fn(); } catch { /* listener errors must not break service */ }
    }
  }

  /** Sync read of any swr-cached entry for the current location. */
  private loadFromCache(): void {
    const loc = this.locationService.getState();
    if (loc.lat === null || loc.lng === null) return;
    const cached = swrPeek<WeatherDetails>(cacheKey(loc.lat, loc.lng));
    if (cached?.data) {
      this.detailsCache = cached.data;
      this.data = sliceForWidget(cached.data);
    }
  }

  /**
   * Fetch with stale-while-revalidate. Returns cached value instantly when
   * available; never blocks the caller waiting on network. Background
   * revalidation updates `this.data` so the next `getData()` reflects the
   * fresh value.
   */
  async fetch(): Promise<WeatherData | null> {
    const loc = this.locationService.getState();
    if (loc.lat === null || loc.lng === null) return null;

    const key = cacheKey(loc.lat, loc.lng);
    const result = await swr<WeatherDetails>(
      key,
      () => this._fetchFromNetwork(loc.lat!, loc.lng!, loc.city || undefined),
      {
        freshTtlMs: FRESH_TTL_MS,
        staleTtlMs: STALE_TTL_MS,
        onUpdate: (full) => {
          this.detailsCache = full;
          this.data = sliceForWidget(full);
          this.notify();
        },
      },
    );
    if (result) {
      this.detailsCache = result;
      this.data = sliceForWidget(result);
      this.notify();
    }
    return this.data;
  }

  /** Fetch full details for an arbitrary location (used by Weather page +
   *  watched cities). Each location gets its own cache key. */
  async fetchDetails(lat: number, lng: number, cityHint?: string): Promise<WeatherDetails | null> {
    const key = cacheKey(lat, lng);
    return swr<WeatherDetails>(
      key,
      () => this._fetchFromNetwork(lat, lng, cityHint),
      { freshTtlMs: FRESH_TTL_MS, staleTtlMs: STALE_TTL_MS },
    );
  }

  /** Sync peek into cache for a non-current location — used by the Weather
   *  page to render watched-city tiles synchronously while a background
   *  fetch warms missing caches. */
  peekDetails(lat: number, lng: number): WeatherDetails | null {
    const cached = swrPeek<WeatherDetails>(cacheKey(lat, lng));
    return cached?.data ?? null;
  }

  /** City search via Open-Meteo Geocoding API. No API key, CORS-clean. */
  async geocodeCity(query: string, lang = 'en'): Promise<GeocodeResult[]> {
    const q = query.trim();
    if (q.length < 2) return [];
    const url =
      `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(q)}` +
      `&count=8&language=${encodeURIComponent(lang)}&format=json`;
    try {
      const resp = await fetch(url, { cache: 'no-store' });
      if (!resp.ok) return [];
      const json = await resp.json();
      return (json.results ?? []) as GeocodeResult[];
    } catch {
      return [];
    }
  }

  /** Combined network call: forecast + air quality, parsed into WeatherDetails. */
  private async _fetchFromNetwork(
    lat: number,
    lng: number,
    city?: string,
  ): Promise<WeatherDetails | null> {
    const forecastUrl =
      `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}` +
      `&current=temperature_2m,apparent_temperature,relative_humidity_2m,weather_code,` +
      `cloud_cover,cloud_cover_low,cloud_cover_mid,cloud_cover_high,pressure_msl,` +
      `wind_speed_10m,wind_direction_10m,is_day,rain,uv_index,visibility,dew_point_2m` +
      `&hourly=temperature_2m,precipitation,precipitation_probability,wind_speed_10m,weather_code` +
      `&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,` +
      `precipitation_probability_max,wind_speed_10m_max,sunrise,sunset,uv_index_max` +
      `&forecast_days=10&timezone=auto`;

    const aqUrl =
      `https://air-quality-api.open-meteo.com/v1/air-quality?latitude=${lat}&longitude=${lng}` +
      `&current=european_aqi,us_aqi,pm10,pm2_5,carbon_monoxide,nitrogen_dioxide,sulphur_dioxide,ozone,` +
      `alder_pollen,birch_pollen,grass_pollen,mugwort_pollen,olive_pollen,ragweed_pollen` +
      `&timezone=auto`;

    // Marine: ~48 h hourly sea level + waves + sea-surface temperature.
    // For inland points the API returns the structure with null arrays —
    // parser detects that and leaves `marine` undefined.
    const marineUrl =
      `https://marine-api.open-meteo.com/v1/marine?latitude=${lat}&longitude=${lng}` +
      `&current=sea_surface_temperature,wave_height,wave_direction,wave_period` +
      `&hourly=sea_level_height_msl,wave_height` +
      `&forecast_days=2&timezone=auto`;

    let forecast: Record<string, unknown> | null = null;
    let aq: Record<string, unknown> | null = null;
    let marine: Record<string, unknown> | null = null;
    try {
      const [fResp, aqResp, mResp] = await Promise.all([
        fetch(forecastUrl, { cache: 'no-store' }),
        fetch(aqUrl, { cache: 'no-store' }).catch(() => null),
        fetch(marineUrl, { cache: 'no-store' }).catch(() => null),
      ]);
      if (!fResp.ok) return null;
      forecast = await fResp.json();
      if (aqResp && aqResp.ok) aq = await aqResp.json();
      if (mResp && mResp.ok) marine = await mResp.json();
    } catch {
      return null;
    }
    if (!forecast) return null;
    return parseForecast(forecast, aq, marine, lat, lng, city);
  }
}

// ---------------------------------------------------------------------------
// Pure parsing helpers — no network, no state.
// ---------------------------------------------------------------------------

function sliceForWidget(d: WeatherDetails): WeatherData {
  const c = d.current;
  const today = d.daily[0];
  // Hourly window: take the next 24 hourly entries from "now" — Open-Meteo
  // returns 240h for forecast_days=10, starting at midnight local; we
  // already trimmed to a "next 24h" rolling window in parseForecast.
  const hourlyTemp: number[] = d.hourly.slice(0, 24).map((h) => h.temp);
  const hourlyRain: { hour: number; rain: number }[] = d.hourly
    .slice(0, 24)
    .map((h, i) => ({ hour: new Date(h.time).getHours() || i, rain: h.rain }));
  return {
    temperature: c.temperature,
    feelsLike: c.feelsLike,
    tempMax: today?.tempMax ?? c.temperature,
    tempMin: today?.tempMin ?? c.temperature,
    rain: c.rain,
    humidity: c.humidity,
    windSpeed: c.windSpeed,
    precipitationProbability: today?.precipitationProbability ?? 0,
    hourlyRain,
    hourlyTemp,
    weatherCode: c.weatherCode,
    fetchedAt: d.fetchedAt,
    city: d.city,
  };
}

function parseForecast(
  json: Record<string, unknown>,
  aq: Record<string, unknown> | null,
  marine: Record<string, unknown> | null,
  lat: number,
  lng: number,
  city?: string,
): WeatherDetails {
  const current = (json.current ?? {}) as Record<string, number | string | undefined>;
  const hourly = (json.hourly ?? {}) as Record<string, unknown>;
  const daily = (json.daily ?? {}) as Record<string, unknown>;

  // Hourly: trim to "next 24 hours" starting at the current hour.
  const hourlyTimes = (hourly.time as string[] | undefined) ?? [];
  const nowMs = Date.now();
  let startIdx = hourlyTimes.findIndex((t) => new Date(t).getTime() >= nowMs);
  if (startIdx < 0) startIdx = 0;
  const sliceEnd = Math.min(hourlyTimes.length, startIdx + 24);
  const hT = (hourly.temperature_2m as number[] | undefined) ?? [];
  const hR = (hourly.precipitation as number[] | undefined) ?? [];
  const hP = (hourly.precipitation_probability as number[] | undefined) ?? [];
  const hW = (hourly.wind_speed_10m as number[] | undefined) ?? [];
  const hC = (hourly.weather_code as number[] | undefined) ?? [];
  const hourlyOut: WeatherDetails['hourly'] = [];
  for (let i = startIdx; i < sliceEnd; i++) {
    hourlyOut.push({
      time: hourlyTimes[i] ?? '',
      temp: hT[i] ?? 0,
      rain: hR[i] ?? 0,
      precipitationProbability: hP[i] ?? 0,
      wind: hW[i] ?? 0,
      weatherCode: hC[i] ?? 0,
    });
  }

  // Daily: 10 entries
  const dT = (daily.time as string[] | undefined) ?? [];
  const dC = (daily.weather_code as number[] | undefined) ?? [];
  const dMax = (daily.temperature_2m_max as number[] | undefined) ?? [];
  const dMin = (daily.temperature_2m_min as number[] | undefined) ?? [];
  const dP = (daily.precipitation_sum as number[] | undefined) ?? [];
  const dPP = (daily.precipitation_probability_max as number[] | undefined) ?? [];
  const dW = (daily.wind_speed_10m_max as number[] | undefined) ?? [];
  const dSr = (daily.sunrise as string[] | undefined) ?? [];
  const dSs = (daily.sunset as string[] | undefined) ?? [];
  const dUv = (daily.uv_index_max as number[] | undefined) ?? [];
  const dailyOut: WeatherDetails['daily'] = dT.map((date, i) => ({
    date,
    weatherCode: dC[i] ?? 0,
    tempMax: dMax[i] ?? 0,
    tempMin: dMin[i] ?? 0,
    precipitation: dP[i] ?? 0,
    precipitationProbability: dPP[i] ?? 0,
    windMax: dW[i] ?? 0,
    sunrise: dSr[i] ?? '',
    sunset: dSs[i] ?? '',
    uvMax: dUv[i] ?? 0,
  }));

  // Air quality + pollen (best-effort — endpoint may be unavailable in some regions)
  let airQuality: WeatherDetails['airQuality'];
  let pollen: WeatherDetails['pollen'];
  if (aq && aq.current && typeof aq.current === 'object') {
    const c = aq.current as Record<string, number | undefined>;
    airQuality = {
      aqiEuropean: c.european_aqi,
      aqiUs: c.us_aqi,
      pm25: c.pm2_5,
      pm10: c.pm10,
      o3: c.ozone,
      no2: c.nitrogen_dioxide,
      so2: c.sulphur_dioxide,
      co: c.carbon_monoxide,
    };
    // Pollen — keep only the species that returned a finite number.
    const pollenRaw = {
      alder: c.alder_pollen,
      birch: c.birch_pollen,
      grass: c.grass_pollen,
      mugwort: c.mugwort_pollen,
      olive: c.olive_pollen,
      ragweed: c.ragweed_pollen,
    };
    const pollenClean: NonNullable<WeatherDetails['pollen']> = {};
    let anyPollen = false;
    for (const [k, v] of Object.entries(pollenRaw)) {
      if (typeof v === 'number' && Number.isFinite(v)) {
        (pollenClean as Record<string, number>)[k] = v;
        anyPollen = true;
      }
    }
    if (anyPollen) pollen = pollenClean;
  }

  // Marine — only set when we got non-null sea-level data (coastal points).
  const marineDetails = marine ? parseMarine(marine) : undefined;

  // Moon — pure-JS calculation (no API). Keyed off "now"; phase changes
  // slowly enough that the cached value is good for the SWR window.
  const lunar = getLunarState(new Date());

  return {
    city,
    lat,
    lng,
    fetchedAt: Date.now(),
    current: {
      temperature: Number(current.temperature_2m ?? 0),
      feelsLike: Number(current.apparent_temperature ?? current.temperature_2m ?? 0),
      weatherCode: Number(current.weather_code ?? 0),
      humidity: Number(current.relative_humidity_2m ?? 0),
      windSpeed: Number(current.wind_speed_10m ?? 0),
      windDirection: Number(current.wind_direction_10m ?? 0),
      pressure: Number(current.pressure_msl ?? 0),
      cloudCover: Number(current.cloud_cover ?? 0),
      cloudCoverLow: numberOrUndef(current.cloud_cover_low),
      cloudCoverMid: numberOrUndef(current.cloud_cover_mid),
      cloudCoverHigh: numberOrUndef(current.cloud_cover_high),
      visibility: numberOrUndef(current.visibility),
      dewPoint: numberOrUndef(current.dew_point_2m),
      isDay: Number(current.is_day ?? 1) === 1,
      rain: Number(current.rain ?? 0),
      uvIndex: Number(current.uv_index ?? 0),
    },
    hourly: hourlyOut,
    daily: dailyOut,
    airQuality,
    pollen,
    marine: marineDetails,
    moon: {
      phaseFraction: lunar.phaseFraction,
      illumination: lunar.illumination,
      nameKey: lunarLabelKey(lunar),
      emoji: lunar.emoji,
    },
  };
}

function numberOrUndef(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

/** Parse the Open-Meteo marine response. Returns undefined for inland
 *  locations (the API still responds 200 but with null hourly arrays). */
function parseMarine(json: Record<string, unknown>): WeatherDetails['marine'] | undefined {
  const hourly = (json.hourly ?? {}) as Record<string, unknown>;
  const times = (hourly.time as string[] | undefined) ?? [];
  const heights = (hourly.sea_level_height_msl as Array<number | null> | undefined) ?? [];
  const validHeights = heights.filter((h): h is number => typeof h === 'number');
  if (times.length === 0 || validHeights.length < 4) return undefined;

  const series: Array<{ time: string; height: number }> = [];
  for (let i = 0; i < times.length; i++) {
    const h = heights[i];
    if (typeof h === 'number') series.push({ time: times[i], height: h });
  }
  if (series.length < 4) return undefined;

  // Local extrema = tide events. We compare each hourly point to its
  // neighbors — first-derivative sign change. Endpoints excluded. With
  // the Open-Meteo MSL series at 1-hour resolution, this lands within
  // ~30 min of true high/low water (acceptable for app UX).
  const tides: NonNullable<WeatherDetails['marine']>['tides'] = [];
  for (let i = 1; i < series.length - 1; i++) {
    const prev = series[i - 1].height;
    const cur = series[i].height;
    const next = series[i + 1].height;
    if (cur > prev && cur > next) tides.push({ ...series[i], type: 'high' });
    else if (cur < prev && cur < next) tides.push({ ...series[i], type: 'low' });
  }

  const cur = (json.current ?? {}) as Record<string, number | string | undefined>;
  return {
    seaSurfaceTemp: numberOrUndef(cur.sea_surface_temperature),
    waveHeight: numberOrUndef(cur.wave_height),
    waveDirection: numberOrUndef(cur.wave_direction),
    wavePeriod: numberOrUndef(cur.wave_period),
    seaLevel: series,
    tides,
  };
}

export type { GeocodeResult };
